import { Info } from '@mui/icons-material';
import { ButtonBase, styled, type SxProps, type Theme } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, type TooltipProps } from './index';

const InfoButton = styled(ButtonBase)(({ theme }) => ({
  borderRadius: '50%',
  color: theme.palette.secondary.main,
  cursor: 'help',
  display: 'inline-flex',
  verticalAlign: 'middle',
  '&:focus-visible': theme.mixins.focusRing,
}));

export interface InfoTooltipProps {
  /** The explanation the tooltip shows; it becomes the control's accessible description. */
  title: ReactNode;
  /** The control's name. Defaults to "More information". */
  label?: string;
  icon?: ReactElement;
  placement?: TooltipProps['placement'];
  sx?: SxProps<Theme>;
}

/**
 * A help icon whose explanation lives in a tooltip. The icon is a focusable control, so a
 * keyboard user can reach the tooltip, and a screen reader hears the explanation as the
 * control's description (WCAG 1.1.1, 2.1.1).
 */
export const InfoTooltip = ({ icon, label, placement, sx, title }: InfoTooltipProps) => {
  const { t } = useTranslation();
  return (
    <Tooltip placement={placement} title={title}>
      <InfoButton
        aria-label={label ?? t('common.tooltips.more_information')}
        disableRipple
        sx={sx}
        type="button"
      >
        {icon ?? <Info fontSize="small" />}
      </InfoButton>
    </Tooltip>
  );
};
